/** Colour-mode persistence, kept separate from React so it can be tested directly. */

export type ColorMode = 'light' | 'dark'

export const COLOR_MODE_STORAGE_KEY = 'internal-app-intake-theme'

/** Whether a stored value is a mode we recognise. */
function isColorMode(value: unknown): value is ColorMode {
  return value === 'light' || value === 'dark'
}

/**
 * The mode to start in.
 *
 * An explicit stored choice always wins. Failing that we follow the OS, which
 * differs from untool-mage (it hard-defaults to light) — with no chrome to
 * explain itself, a form that already matches the submitter's system is a better
 * first impression than one that flashes bright and makes them hunt for a toggle.
 *
 * `prefersDark` is passed in rather than read here so this stays a pure function;
 * the caller supplies it from `useMediaQuery`.
 */
export function initialColorMode(
  prefersDark: boolean,
  storage: Pick<Storage, 'getItem'> | null
): ColorMode {
  const stored = readStoredColorMode(storage)
  if (stored) {
    return stored
  }
  return prefersDark ? 'dark' : 'light'
}

/** Read a previously chosen mode, tolerating unavailable or corrupt storage. */
export function readStoredColorMode(
  storage: Pick<Storage, 'getItem'> | null
): ColorMode | null {
  if (!storage) {
    return null
  }
  try {
    const stored = storage.getItem(COLOR_MODE_STORAGE_KEY)
    return isColorMode(stored) ? stored : null
  } catch {
    // Storage can throw when cookies/site data are blocked. A missing preference
    // is not worth breaking the page over.
    return null
  }
}

/** Persist a chosen mode, ignoring storage failures for the same reason. */
export function storeColorMode(
  mode: ColorMode,
  storage: Pick<Storage, 'setItem'> | null
): void {
  if (!storage) {
    return
  }
  try {
    storage.setItem(COLOR_MODE_STORAGE_KEY, mode)
  } catch {
    // Preference simply won't survive a reload.
  }
}
